import React, {
  useCallback,
  useEffect,
  useState,
  useReducer,
  useRef,
  useLayoutEffect,
} from 'react'
import {
  ArrowLeftOutlined,
  CloseOutlined,
  ExceptionOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from '@ant-design/icons'
import {
  Card,
  Col,
  Row,
  Pagination,
  Typography,
  Button,
  Spin,
  Tooltip,
  Badge,
  Tag,
} from 'antd'
import Modal from 'antd/lib/modal/Modal'
import { ListItems } from 'forms/SearchSong/FormSearchSong'
import { SearchInput } from 'components/SearchInput'
import {
  capitalize,
  isEmpty,
  join,
  map,
  slice,
  split,
  get,
  includes,
  chunk,
  keys,
  words,
  size,
  isNumber,
  round,
  isString,
} from 'lodash'
import { useFacets, useSearchIndex } from 'utils/algolia'
import { useFacetsBuildQuery } from './hooks'
import { SearchProvider, useSearchBook } from './context'
import './styles.scss'

const OBoxArea = 130 * 130
const OBox = ({ name, value, onClick, active }) => {
  return (
    <Col flex="120px">
      <Card
        hoverable
        className={`FacetBox ${active ? 'active' : ''}`}
        onClick={() => onClick(name, value)}
      >
        <Typography.Title level={5}>
          {words(capitalize(name)).join(' ')}
        </Typography.Title>
        <Card.Meta description={isNaN(value) ? size(value) : value} />
      </Card>
    </Col>
  )
}

const SectionFacets = ({ results }) => {
  const facetBoxRef = useRef()
  const { facets, loading } = useFacets()
  const [currentNavFacet, setCurrentNavFacet] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [hitsPerPage, setHitsPerPage] = useState(8)
  const {
    handleFacet,
    facetState,
    setFacetState,
    filtersQuery,
  } = useSearchBook()

  const inFacets = isEmpty(currentNavFacet)
    ? facets
    : get(facets, currentNavFacet)
  const mappedFacets = keys(inFacets).sort()
  const pages = chunk(mappedFacets, hitsPerPage)
  const hits = map(pages[currentPage - 1], (name) => ({
    name,
    value: inFacets[name],
  }))
  const nbPages = pages.length

  const { searchFilters } = useFacetsBuildQuery(facetState)

  const handleNavFacet = useCallback(
    (attribute, value) => {
      setCurrentNavFacet((facetName) => {
        if (isNumber(value)) {
          // Set the selected facet/value
          handleFacet(facetName, attribute)
          return facetName
        }
        return attribute
      })
    },
    [handleFacet]
  )
  const handleNavBackFacet = useCallback((name) => {
    setCurrentNavFacet((path) => {
      const paths = split(path, '.')
      if (paths.length < 1) return paths

      return join(slice(paths, 0, paths.length - 1), '.')
    })
  }, [])
  const handlFacetReset = useCallback(() => {
    setCurrentNavFacet('')
    setFacetState({})
  }, [])

  const resizeFacetsPerPage = useCallback(() => {
    const el = facetBoxRef.current.parentElement
    const offsetWidth = el.offsetWidth
    const offsetHeight = el.offsetHeight

    const facetBoxArea = offsetWidth * offsetHeight
    const maxOBoxPerArea = round(facetBoxArea / OBoxArea) - 4
    setHitsPerPage(maxOBoxPerArea)
  }, [])

  useEffect(() => {
    if (currentPage > nbPages) setCurrentPage(1)
  }, [nbPages])

  useEffect(() => {
    if (!facetBoxRef.current) return

    const observer = new ResizeObserver(resizeFacetsPerPage)
    observer.observe(facetBoxRef.current.parentElement, {
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['style'],
    })

    return () => {
      // facetBoxRef.current.removeEventListener('resize', resizeFacetsPerPage)
      observer.disconnect()
    }
  }, [facetBoxRef.current])

  return (
    <>
      <Row gutter={[8, 8]} ref={facetBoxRef} style={{ maxHeight: '80vh' }}>
        {isEmpty(results) && (
          <Col flex="120px">
            <Card hoverable className="FacetBox" onClick={handlFacetReset}>
              <ExceptionOutlined />
              <Card.Meta title="Reset search" />
            </Card>
          </Col>
        )}
        {!isEmpty(currentNavFacet) && (
          <Col flex="120px">
            <Card hoverable className="FacetBox" onClick={handleNavBackFacet}>
              <ArrowLeftOutlined />
            </Card>
          </Col>
        )}
        {isEmpty(currentNavFacet)
          ? map(hits, ({ name, value }) => (
              <OBox
                name={name}
                value={value}
                onClick={handleNavFacet}
                active={
                  !isEmpty(
                    get(
                      facetState,
                      isEmpty(currentNavFacet)
                        ? name
                        : `${currentNavFacet}.${name}`
                    )
                  )
                }
                key={name}
              />
            ))
          : map(hits, ({ name, value }) => (
              <OBox
                name={name}
                value={value}
                onClick={handleNavFacet}
                active={!!includes(get(facetState, currentNavFacet), name)}
                key={name}
              />
            ))}
      </Row>
      <Pagination
        pageSize={hitsPerPage}
        total={mappedFacets.length}
        current={currentPage}
        showSizeChanger={false}
        onChange={setCurrentPage}
        className="Results-Pagin"
        size="small"
      />
    </>
  )
}

const SectionActiveFacets = () => {
  const { facetState } = useSearchBook()

  return (
    <>
      {map(facetState, (values, attribute) => (
        <Tag>
          {attribute}: {join(values, ', ')}
        </Tag>
      ))}
    </>
  )
}
export const SearchBook = ({
  visible,
  onChange,
  handleClose: handleCloseModal,
  onSelect,
}) => {
  const [sortType, toggleSortType] = useReducer(
    (state, force) =>
      !isString(force) ? (state !== 'Asc' ? 'Asc' : 'Desc') : force,
    ''
  )
  const [currentQuery, setCurrentQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const { filtersQuery, handlFacetReset } = useSearchBook()

  const { loading, results, nbPages, page } = useSearchIndex({
    type: `song${sortType}`,
    query: currentQuery,
    filters: filtersQuery,
    page: currentPage - 1,
  })

  const handleSelect = useCallback(
    (item) => {
      onSelect(item)
      handleCloseModal()
    },
    [handleCloseModal, onSelect]
  )
  const handleReset = useCallback(() => {
    handlFacetReset()
    toggleSortType('')
  }, [handlFacetReset])

  return (
    <Modal
      visible={visible}
      width="100%"
      closable={false}
      footer={null}
      maskClosable
      onCancel={handleCloseModal}
    >
      <Row gutter={[8, 8]}>
        <Col flex="1" style={{ display: 'flex' }}>
          <SearchInput value={currentQuery} onChange={setCurrentQuery} />
          <SectionActiveFacets />
          <Tooltip
            title={sortType !== 'Asc' ? 'Sort from A-Z' : 'Sort from Z-A'}
          >
            <Button
              icon={
                sortType !== 'Asc' ? (
                  <SortAscendingOutlined />
                ) : (
                  <SortDescendingOutlined />
                )
              }
              onClick={toggleSortType}
            />
          </Tooltip>
        </Col>
        <Col>
          <Button icon={<CloseOutlined />} onClick={handleReset} />
        </Col>
      </Row>
      <Spin size="large" spinning={loading}>
        <Row gutter={16}>
          <Col flex="300px">
            <ListItems
              results={results}
              nbPages={nbPages}
              page={currentPage}
              onPageChange={setCurrentPage}
              onSelect={handleSelect}
            />
          </Col>
          <Col flex="1">
            <SectionFacets results={results} />
          </Col>
        </Row>
      </Spin>
    </Modal>
  )
}
